import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getMovieById, getRelated } from "@/utils/movieUtils";
import RelatedList from "@/pages/detail/components/RelatedList";

type PlayerStatus = "loading" | "ready" | "error";

export default function PlayPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const movie = useMemo(() => (id ? getMovieById(id) : undefined), [id]);

  const episode = Number(searchParams.get("ep") || "1");
  const [status, setStatus] = useState<PlayerStatus>("loading");
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);

  usePageMeta({
    title: movie ? `播放《${movie.title}》 - 伦理影院` : "在线播放 - 伦理影院",
    description: movie
      ? `在线观看《${movie.title}》演示播放页，支持剧集切换、播放暂停与进度展示。本页为前端模拟播放演示，不接入真实视频流。`
      : "伦理影院在线播放演示页",
    keywords: movie ? `${movie.title},在线观看,伦理影院,伦理影视` : "伦理影院,在线播放",
  });

  // simulate loading every time the episode changes
  useEffect(() => {
    setStatus("loading");
    setPlaying(false);
    setProgress(0);
    const t = window.setTimeout(() => setStatus("ready"), 1200);
    return () => window.clearTimeout(t);
  }, [episode, id]);

  // fake progress bar while "playing"
  useEffect(() => {
    if (status !== "ready" || !playing) {
      if (timerRef.current) window.clearInterval(timerRef.current);
      return;
    }
    timerRef.current = window.setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 0.5));
    }, 300);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [status, playing]);

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该影视内容</h1>
          <Link
            to="/category?type=movie"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回分类页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelated(movie, 6);
  const currentEp = movie.type === "tv" ? Math.min(Math.max(episode, 1), movie.episodes) : 1;

  const selectEpisode = (ep: number) => {
    setSearchParams(ep > 1 ? { ep: String(ep) } : {});
  };

  const simulateError = () => {
    setPlaying(false);
    setStatus("error");
  };

  const retry = () => setStatus("loading");

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8">
        <nav aria-label="面包屑" className="pt-6 text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to={`/detail/${movie.id}`} className="transition-colors hover:text-primary-600">
                {movie.title}
              </Link>
            </li>
            <li>/</li>
            <li className="text-foreground-800">在线播放</li>
          </ol>
        </nav>

        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          {movie.title}
          {movie.type === "tv" && <span className="ml-2 text-base font-medium text-foreground-600">第 {currentEp} 集</span>}
        </h1>

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
              <img src={movie.cover} alt={`${movie.title} 播放画面`} title={`${movie.title} 伦理影视`} className="h-full w-full object-top opacity-40" />

              {status === "loading" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/80 text-background-50">
                  <i className="ri-loader-4-line animate-spin text-3xl"></i>
                  <p className="text-sm">正在加载播放资源…</p>
                </div>
              )}

              {status === "error" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/90 px-4 text-center text-background-50">
                  <i className="ri-error-warning-line text-3xl text-accent-400"></i>
                  <p className="text-sm">播放资源加载失败，请稍后重试</p>
                  <button
                    type="button"
                    onClick={retry}
                    className="mt-1 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-refresh-line"></i> 重新加载
                  </button>
                </div>
              )}

              {status === "ready" && (
                <>
                  {!playing && (
                    <button
                      type="button"
                      aria-label="播放"
                      onClick={() => setPlaying(true)}
                      className="absolute inset-0 flex items-center justify-center bg-foreground-950/40"
                    >
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-primary-950 transition-transform hover:scale-105">
                        <i className="ri-play-fill text-3xl"></i>
                      </span>
                    </button>
                  )}

                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/90 to-transparent px-4 pb-3 pt-8">
                    <div className="h-1 w-full overflow-hidden rounded-full bg-background-50/30">
                      <div className="h-full rounded-full bg-accent-400" style={{ width: `${progress}%` }}></div>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-background-50">
                      <button
                        type="button"
                        aria-label={playing ? "暂停" : "播放"}
                        onClick={() => setPlaying((p) => !p)}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50/15 transition-colors hover:bg-background-50/30"
                      >
                        <i className={playing ? "ri-pause-fill text-lg" : "ri-play-fill text-lg"}></i>
                      </button>
                      <span className="text-xs text-background-100">{progress.toFixed(0)}%</span>
                      <span className="ml-auto text-xs text-background-100">
                        {movie.type === "movie" ? "电影 · 单集" : `第 ${currentEp} / ${movie.episodes} 集`}
                      </span>
                      <button
                        type="button"
                        aria-label="模拟加载错误"
                        onClick={simulateError}
                        className="text-xs text-background-100 underline-offset-2 hover:underline"
                      >
                        模拟错误
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-xs text-accent-900">
              <i className="ri-information-line text-base"></i>
              演示提示：本页为前端模拟播放器，仅用于展示播放交互流程，不提供也不接入任何真实视频内容。
            </div>

            {movie.type === "tv" && (
              <section className="mt-8">
                <h2 className="font-heading text-lg font-bold text-foreground-950">剧集选择</h2>
                <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
                  {Array.from({ length: Math.min(movie.episodes, 24) }, (_, i) => i + 1).map((ep) => (
                    <button
                      key={ep}
                      type="button"
                      onClick={() => selectEpisode(ep)}
                      className={`flex h-10 items-center justify-center rounded-md border text-sm transition-colors ${
                        ep === currentEp
                          ? "border-primary-500 bg-primary-500 text-background-50"
                          : "border-background-300/60 bg-background-100 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                      }`}
                    >
                      第{ep}集
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span key={tag} className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    {tag}
                  </span>
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <RelatedList movies={related} />
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}